import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryState,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
} from '@urbanx/agx.activities.client';
import { TaskModalPage } from 'pages/allTasks/TaskModal/taskModalPageStatus';

type ResolvedTask = AutopilotTaskDto & { id: string };

const pageStepOrders = [
  Array.from({ length: 14 }, (_, index) => index + 1),
  [15],
  [16, 17, 18, 19],
  [20, 21, 22, 23, 24],
];

const requireCanonicalTask = (tasks: AutopilotTaskDto[]): ResolvedTask => {
  if (tasks.length !== 1) {
    throw new Error(
      `Contract Compliance requires exactly one canonical task; received ${tasks.length}`
    );
  }

  const task = tasks[0];
  if (!task?.id) throw new Error('Contract Compliance task is missing an ID');
  if (
    task.category?.workflowType !== TaskCategoryWorkflowType.Contract ||
    task.category.stage !== TaskCategoryStage.ContractCompliance ||
    task.category.taskType !== TaskCategoryTaskType.ContractCompliance ||
    task.category.state !== TaskCategoryState.QLD
  ) {
    throw new Error('Contract Compliance task does not match the canonical QLD category');
  }

  const steps = [...(task.taskSteps ?? [])].sort(
    (left, right) => (left.order ?? 0) - (right.order ?? 0)
  );
  if (steps.length !== 24 || steps.some((step, index) => step.order !== index + 1)) {
    throw new Error('Contract Compliance task does not match its 24-step canonical layout');
  }

  return { ...task, taskSteps: steps } as ResolvedTask;
};

const pageFor = (task: ResolvedTask, orders: number[], pageIndex: number): TaskModalPage => {
  const pageTask: ResolvedTask = {
    ...task,
    id: `${task.id}:contract-compliance-page-${pageIndex + 1}`,
    taskSteps: orders.map((order) => task.taskSteps![order - 1]!),
  };

  return {
    task: pageTask,
    completionTasks: [pageTask],
    stepBindings: orders.map((order) => ({
      pageStepOrder: order,
      taskId: task.id,
      taskStepOrder: order,
    })),
  };
};

export const resolveContractCompliancePages = (tasks: AutopilotTaskDto[]): TaskModalPage[] => {
  const task = requireCanonicalTask(tasks);
  return pageStepOrders.map((orders, index) => pageFor(task, orders, index));
};
